import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { cn } from "../lib/utils";
import { Text, type TextProps } from "../atoms/text";

/** Shared heading pairs. Layout, actions and interactive behavior belong to the caller. */
export const TitleDescription = forwardRef<
  HTMLDivElement,
  TitleDescriptionProps
>(function TitleDescription(
  {
    title,
    description,
    appearance,
    titleAs: Heading,
    titleId,
    titleProps,
    descriptionProps,
    titlePrefix,
    titleSuffix,
    wrapTitle = false,
    titleClassName,
    descriptionClassName,
    className,
    ...props
  },
  ref,
) {
  const style = appearances[appearance];
  const heading = (
    <Heading
      id={titleId}
      className={cn(style.title, titleClassName)}
      {...titleProps}
    >
      {title}
    </Heading>
  );

  return (
    <div ref={ref} className={className} {...props}>
      {appearance === "video" || titlePrefix || titleSuffix ? (
        <div
          className={cn("flex items-center gap-2", wrapTitle && "flex-wrap")}
        >
          {titlePrefix}
          {heading}
          {titleSuffix}
        </div>
      ) : (
        heading
      )}
      {description && (
        <Text
          {...(descriptionProps ?? style.description)}
          className={cn(style.spacing, descriptionClassName)}
        >
          {description}
        </Text>
      )}
    </div>
  );
});

const appearances = {
  page: {
    title: "heading-xl",
    description: { variant: "body-md", tone: "secondary" },
    spacing: "mt-2",
  },
  section: {
    title: "heading-md",
    description: { variant: "body-sm", tone: "secondary" },
    spacing: "mt-1",
  },
  video: {
    title: "text-base font-semibold text-foreground",
    description: { size: "xs", tone: "muted-foreground" },
    spacing: "mt-0.5",
  },
  compact: {
    title: "text-sm font-semibold",
    description: { size: "xs", tone: "muted-foreground" },
    spacing: undefined,
  },
  subsection: {
    title: "text-lg font-semibold",
    description: { size: "sm", tone: "muted-foreground" },
    spacing: undefined,
  },
  "small-section": {
    title: "heading-sm",
    description: { variant: "body-sm", tone: "muted-foreground" },
    spacing: "mt-1",
  },
  "application-section": {
    title: "heading-md",
    description: { variant: "body-md", tone: "muted-foreground" },
    spacing: "mt-2",
  },
  "large-section": {
    title: "heading-lg",
    description: { variant: "body-md", tone: "secondary" },
    spacing: "mt-2",
  },
  "label-section": {
    title: "body-md font-semibold",
    description: { variant: "body-sm", tone: "secondary" },
    spacing: "mt-1",
  },
  eyebrow: {
    title: "text-xs font-medium uppercase tracking-wider text-muted-foreground",
    description: { size: "sm", tone: "muted-foreground" },
    spacing: "mt-1",
  },
  "legacy-section": {
    title: "text-xl font-semibold",
    description: { size: "sm", tone: "muted-foreground" },
    spacing: "mt-2",
  },
  // Preserve existing admin page typography while those screens adopt AdminPage.
  "legacy-page": {
    title: "text-2xl font-bold mb-2",
    description: { tone: "muted-foreground" },
    spacing: undefined,
  },
} as const;

type TitleDescriptionProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "title" | "children"
> & {
  title: ReactNode;
  description?: ReactNode;
  appearance: keyof typeof appearances;
  titleAs: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /** Preserve the target of aria-labelledby and heading links. */
  titleId?: string;
  /** Preserve heading attributes. An explicit className replaces preset typography. */
  titleProps?: Omit<ComponentPropsWithoutRef<"h2">, "children" | "id"> &
    DataAttributes;
  /** When supplied, replaces preset description typography; spacing stays with the appearance. */
  descriptionProps?: TextProps & {
    children?: never;
    className?: never;
  } & DataAttributes;
  titlePrefix?: ReactNode;
  titleSuffix?: ReactNode;
  wrapTitle?: boolean;
  titleClassName?: string;
  descriptionClassName?: string;
};

type DataAttributes = {
  [attribute: `data-${string}`]: string | number | boolean | undefined;
};
